import { Badge } from "@/components/ui/badge";
import { TrendingDown, TrendingUp, Minus, BarChart3 } from "lucide-react";

interface SectorComparisonData {
  category: string;
  userTotal: number;
  sectorAverage: number;
  percentile: string;
  difference: string;
}

interface CategorySectorComparisonProps {
  data: SectorComparisonData[];
}

/**
 * CategorySectorComparison Component
 * Displays how the user's emissions compare to sector averages by category
 */
export function CategorySectorComparison({ data }: CategorySectorComparisonProps) {
  if (data.length === 0) {
    return (
      <div className="p-4 bg-muted/50 rounded-md text-center">
        <p className="text-sm text-muted-foreground">
          Add emissions to see how you compare to your sector
        </p>
      </div>
    );
  }

  const getPercentileColor = (percentile: string) => {
    switch (percentile) {
      case "Top 20%":
        return "bg-green-500";
      case "Top 40%":
        return "bg-blue-500";
      case "Average":
        return "bg-yellow-500";
      case "Below Average":
        return "bg-orange-500";
      case "Bottom 20%":
        return "bg-red-500";
      default:
        return "bg-gray-400";
    }
  };

  const getTrendIcon = (difference: string) => {
    const diff = parseFloat(difference);
    if (diff < -10) return <TrendingDown className="h-4 w-4 text-green-600" />;
    if (diff > 10) return <TrendingUp className="h-4 w-4 text-red-600" />;
    return <Minus className="h-4 w-4 text-gray-600" />;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-primary" />
        <h3 className="font-semibold">Sector Comparison</h3>
      </div>
      
      <div className="grid grid-cols-1 gap-3">
        {data.map((item) => {
          const diff = parseFloat(item.difference);
          const isGood = diff < 0; // Lower emissions = better
          
          return (
            <div key={item.category} className="p-3 border rounded-md space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{item.category}</span>
                <Badge variant="secondary" className={`${getPercentileColor(item.percentile)} text-white`}>
                  {item.percentile}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-muted-foreground">Your emissions</p>
                  <p className="font-semibold">{item.userTotal.toFixed(4)} tCO₂e</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Sector average</p>
                  <p className="font-semibold">{item.sectorAverage.toFixed(4)} tCO₂e</p>
                </div>
              </div>
              
              <div className={`flex items-center gap-2 p-2 rounded-md ${
                isGood ? 'bg-green-50 dark:bg-green-950/20' : 'bg-red-50 dark:bg-red-950/20'
              }`}>
                {getTrendIcon(item.difference)}
                <span className={`text-xs font-medium ${
                  isGood ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                }`}>
                  {diff > 0 ? '+' : ''}{diff}% vs sector
                </span>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-md">
        <p className="text-xs text-blue-800 dark:text-blue-200">
          💡 <strong>Tip:</strong> Focus on categories where you're above average to maximize your impact
        </p>
      </div>
    </div>
  );
}

